/**
 * DirectorSearchPage — MCA21 Director Name Search + Bulk Screening
 * ================================================================
 * Flow:
 *   1. User types a director name → click Search
 *   2. Backend returns N persons sharing that name (each with DIN + companies tree)
 *   3. User expands persons / selects companies via checkboxes
 *   4. Click "Screen All Selected" → bulk MCA21 God-Mode lookup
 *   5. See risk distribution + per-company results
 */
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Users, Search, Loader2, ChevronDown, ChevronRight, Building2,
  ShieldAlert, ShieldCheck, AlertTriangle, ScanLine, CheckCircle2,
  Network, Briefcase, Calendar, MapPin, Hash, FileText, X, Eye,
} from "lucide-react";

const API = `${window.location.origin}/api/mca21`;

const RISK_COLOR = {
  LOW: "#22c55e", MEDIUM: "#f59e0b", HIGH: "#ef4444", CRITICAL: "#dc2626",
};
const CARD = { background: "#0d1117", border: "1px solid #1e2530", borderRadius: 12, padding: 18 };
const LABEL = { color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 };

export default function DirectorSearchPage() {
  const [name, setName] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [expanded, setExpanded] = useState({});           // {din: bool}
  const [selectedCins, setSelectedCins] = useState({});   // {cin: bool}
  const [bulkScreening, setBulkScreening] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const persons = searchResult?.persons || [];
  const totalSelected = useMemo(
    () => Object.values(selectedCins).filter(Boolean).length,
    [selectedCins]
  );

  const doSearch = useCallback(async () => {
    if (!name.trim()) return;
    setSearching(true);
    setSearchResult(null);
    setExpanded({});
    setSelectedCins({});
    setBulkResult(null);
    try {
      const { data } = await axios.post(`${API}/director-search`, { name: name.trim() }, { withCredentials: true });
      setSearchResult(data.result);
      // Auto-expand first 3 persons
      const exp = {};
      (data.result.persons || []).slice(0, 3).forEach(p => { exp[p.din] = true; });
      setExpanded(exp);
      toast.success(`${data.result.total_persons_found} persons found for "${name}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Director search failed");
    } finally {
      setSearching(false);
    }
  }, [name]);

  const togglePerson = (din) => setExpanded(p => ({ ...p, [din]: !p[din] }));

  const toggleAllForPerson = (person, on) => {
    setSelectedCins(prev => {
      const next = { ...prev };
      (person.companies_tree || []).forEach(c => {
        if (c.cin) next[c.cin] = on;
      });
      return next;
    });
  };

  const toggleCin = (cin) => setSelectedCins(p => ({ ...p, [cin]: !p[cin] }));

  const screenAll = async () => {
    const cins = Object.keys(selectedCins).filter(c => selectedCins[c]);
    if (cins.length === 0) {
      toast.error("Select at least one company");
      return;
    }
    if (cins.length > 25) {
      toast.error("Max 25 companies per batch — uncheck some");
      return;
    }
    setBulkScreening(true);
    setBulkResult(null);
    try {
      const { data } = await axios.post(`${API}/screen-companies`, { cins }, { withCredentials: true });
      setBulkResult(data);
      toast.success(`Screened ${data.summary.ok}/${data.summary.total} — ${data.summary.total_critical_or_high} flagged HIGH/CRITICAL`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk screening failed");
    } finally {
      setBulkScreening(false);
    }
  };

  const screenAllForPerson = (person) => {
    const cins = (person.companies_tree || []).map(c => c.cin).filter(Boolean);
    const next = {};
    cins.forEach(c => { next[c] = true; });
    setSelectedCins(next);
    setTimeout(screenAll, 100);
  };

  return (
    <div data-testid="director-search-page" style={{ color: "#f1f5f9" }}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <Users style={{ width: 26, height: 26, color: "#a855f7" }} />
        <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }} data-testid="director-search-title">Director Search</h1>
      </div>
      <p style={{ color: "#94a3b8", fontSize: 13, marginTop: 4, marginBottom: 22 }}>
        Find all persons sharing a director name across MCA21 · disambiguate by DIN · bulk-screen their portfolios
      </p>

      {/* Search bar */}
      <div style={{ ...CARD, marginBottom: 18 }}>
        <div style={LABEL}>Director Name</div>
        <div className="flex gap-2 mt-2">
          <input
            data-testid="director-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doSearch()}
            placeholder="e.g. Rajesh Sharma, Priya Patel, Ratan Tata"
            style={{
              flex: 1, background: "#080c12", border: "1px solid #1e2530", borderRadius: 8,
              padding: "11px 14px", color: "#f1f5f9", fontSize: 14, outline: "none",
            }}
          />
          <button
            data-testid="director-search-btn"
            onClick={doSearch}
            disabled={searching || !name.trim()}
            style={{
              background: "linear-gradient(135deg, #a855f7, #7c3aed)", color: "#fff", border: "none",
              padding: "11px 22px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 7, opacity: (searching || !name.trim()) ? 0.5 : 1,
            }}
          >
            {searching ? <Loader2 className="animate-spin" size={14} /> : <Search size={14} />}
            Search MCA21
          </button>
        </div>
        <div style={{ marginTop: 10, fontSize: 11, color: "#64748b" }}>
          Powered by MCA21 V3 Director Search + <code style={{ color: "#a855f7" }}>getDirectorMasterData</code> · Claude Sonnet 4.5
        </div>
      </div>

      {/* Results header / counts */}
      {searchResult && (
        <div style={{ ...CARD, marginBottom: 14, background: "linear-gradient(135deg, rgba(168,85,247,0.06), rgba(124,58,237,0.02))" }} data-testid="director-search-summary">
          <div className="flex items-center justify-between flex-wrap" style={{ gap: 12 }}>
            <div className="flex items-center gap-4 flex-wrap">
              <Counter label="Total Persons" value={searchResult.total_persons_found} color="#a855f7" testid="counter-total" />
              <Counter label="Exact Match" value={searchResult.exact_name_matches} color="#22c55e" testid="counter-exact" />
              <Counter label="Similar Match" value={searchResult.similar_name_matches} color="#f59e0b" testid="counter-similar" />
              <Counter label="Selected" value={totalSelected} color="#3b82f6" testid="counter-selected" />
            </div>
            <button
              data-testid="screen-all-selected-btn"
              onClick={screenAll}
              disabled={bulkScreening || totalSelected === 0}
              style={{
                background: totalSelected > 0 ? "linear-gradient(135deg, #ef4444, #dc2626)" : "#1e2530",
                color: "#fff", border: "none", padding: "10px 20px", borderRadius: 8,
                fontSize: 12, fontWeight: 700, cursor: totalSelected > 0 ? "pointer" : "not-allowed",
                display: "inline-flex", alignItems: "center", gap: 7,
                opacity: bulkScreening ? 0.6 : 1,
              }}
            >
              {bulkScreening ? <Loader2 className="animate-spin" size={13} /> : <ScanLine size={13} />}
              Screen All Selected ({totalSelected})
            </button>
          </div>
          {searchResult.disambiguation_warning && (
            <div style={{ marginTop: 12, padding: 10, background: "rgba(245,158,11,0.08)", border: "1px solid #f59e0b33", borderRadius: 6, color: "#fcd34d", fontSize: 11, display: "flex", gap: 8, alignItems: "flex-start" }}>
              <AlertTriangle size={12} style={{ marginTop: 2, flexShrink: 0 }} />
              <span>{searchResult.disambiguation_warning}</span>
            </div>
          )}
        </div>
      )}

      {/* Bulk Screening Result */}
      {bulkResult && (
        <div style={{ ...CARD, marginBottom: 14, borderColor: "#3b82f655" }} data-testid="bulk-screen-result">
          <div className="flex items-center justify-between mb-3">
            <strong style={{ fontSize: 13, color: "#3b82f6", display: "inline-flex", alignItems: "center", gap: 7 }}>
              <ShieldCheck size={14} /> Bulk Screening Results — {bulkResult.summary.total} companies
            </strong>
            <button onClick={() => setBulkResult(null)} style={{ background: "transparent", border: "none", color: "#94a3b8", cursor: "pointer" }}><X size={14} /></button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 10, marginBottom: 12 }}>
            {Object.entries(bulkResult.summary.by_risk).map(([level, count]) => (
              <div key={level} style={{ background: "#080c12", border: `1px solid ${RISK_COLOR[level]}33`, borderRadius: 6, padding: 10, textAlign: "center" }} data-testid={`risk-bucket-${level}`}>
                <div style={{ ...LABEL, color: RISK_COLOR[level] }}>{level}</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: RISK_COLOR[level], marginTop: 2 }}>{count}</div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 320, overflowY: "auto" }}>
            {bulkResult.results.map((r, i) => {
              if (!r.ok) {
                return (
                  <div key={i} data-testid={`bulk-row-fail-${i}`} style={{ background: "#080c12", border: "1px solid #ef444433", borderRadius: 6, padding: 10, fontSize: 11, color: "#fca5a5" }}>
                    <strong>{r.cin}</strong> · Error: {r.error}
                  </div>
                );
              }
              const res = r.result || {};
              const ra = res.risk_assessment || {};
              const ci = res.company_identity || {};
              const level = (ra.overall_risk || res.risk_level || "MEDIUM").toUpperCase();
              const c = RISK_COLOR[level] || "#94a3b8";
              return (
                <div key={i} data-testid={`bulk-row-${r.cin}`} style={{ background: "#080c12", border: `1px solid ${c}55`, borderRadius: 6, padding: 10 }}>
                  <div className="flex items-center justify-between flex-wrap" style={{ gap: 8 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#f1f5f9" }}>
                        {ci.company_name || res.company_name || r.cin}
                      </div>
                      <code style={{ fontSize: 10, color: "#64748b" }}>{r.cin}</code>
                      {res.status && <span style={{ ...pillBase, color: "#94a3b8", border: "1px solid #1e2530", marginLeft: 8 }}>{res.status}</span>}
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <span style={{ ...pillBase, background: `${c}18`, color: c, border: `1px solid ${c}55` }}>{level}</span>
                      <span style={{ fontSize: 11, color: "#94a3b8", fontFamily: "ui-monospace, monospace" }}>{ra.risk_score ?? res.risk_score ?? "—"}/100</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Persons */}
      {persons.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {persons.map((p, idx) => <PersonCard key={p.din || idx} person={p} idx={idx}
            expanded={expanded[p.din]} toggle={() => togglePerson(p.din)}
            selectedCins={selectedCins} toggleCin={toggleCin}
            toggleAllForPerson={(on) => toggleAllForPerson(p, on)}
            screenAllForPerson={() => screenAllForPerson(p)}
          />)}
        </div>
      )}

      {/* Empty state */}
      {!searchResult && !searching && (
        <div style={{ ...CARD, padding: 40, textAlign: "center", color: "#64748b" }}>
          <Users size={32} style={{ margin: "0 auto 10px", display: "block", color: "#475569" }} />
          <div style={{ fontSize: 13 }}>Type a director name above and click <strong>Search MCA21</strong></div>
          <div style={{ fontSize: 11, marginTop: 6 }}>Common names like "Rajesh Sharma" return 6-12 persons; unique names return one.</div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function Counter({ label, value, color, testid }) {
  return (
    <div data-testid={testid}>
      <div style={LABEL}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color, marginTop: 2 }}>{value ?? 0}</div>
    </div>
  );
}

function PersonCard({ person, idx, expanded, toggle, selectedCins, toggleCin, toggleAllForPerson, screenAllForPerson }) {
  const riskColor = RISK_COLOR[(person.quick_individual_risk || "LOW").toUpperCase()] || "#22c55e";
  const allSelected = (person.companies_tree || []).every(c => selectedCins[c.cin]);
  const someSelected = (person.companies_tree || []).some(c => selectedCins[c.cin]);

  return (
    <div data-testid={`person-card-${person.din || idx}`} style={{ background: "#0d1117", border: `1px solid ${riskColor}33`, borderRadius: 10 }}>
      {/* Person header */}
      <div onClick={toggle} style={{ padding: 14, cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}>
        {expanded ? <ChevronDown size={16} style={{ color: "#94a3b8", flexShrink: 0 }} /> : <ChevronRight size={16} style={{ color: "#94a3b8", flexShrink: 0 }} />}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="flex items-center gap-2 flex-wrap">
            <span style={{ fontSize: 14, fontWeight: 700, color: "#f1f5f9" }}>{person.full_name}</span>
            <span style={{ ...pillBase, background: person.match_type === "EXACT" ? "rgba(34,197,94,0.15)" : "rgba(245,158,11,0.15)", color: person.match_type === "EXACT" ? "#22c55e" : "#f59e0b", border: `1px solid ${person.match_type === "EXACT" ? "#22c55e55" : "#f59e0b55"}` }}>{person.match_type || "SIMILAR"}</span>
            {person.is_disqualified && <span style={{ ...pillBase, background: "rgba(239,68,68,0.15)", color: "#ef4444", border: "1px solid #ef444455" }}>DISQUALIFIED</span>}
            <span style={{ ...pillBase, background: `${riskColor}18`, color: riskColor, border: `1px solid ${riskColor}55` }}>{(person.quick_individual_risk || "LOW").toUpperCase()}</span>
          </div>
          <div style={{ display: "flex", gap: 14, marginTop: 4, fontSize: 11, color: "#94a3b8", flexWrap: "wrap" }}>
            <span><Hash size={10} style={{ display: "inline", marginRight: 3 }} /><code style={{ color: "#a855f7" }}>DIN {person.din}</code></span>
            {person.age && <span><Calendar size={10} style={{ display: "inline", marginRight: 3 }} />{person.age}y</span>}
            {person.address?.city && <span><MapPin size={10} style={{ display: "inline", marginRight: 3 }} />{person.address.city}, {person.address.state}</span>}
            <span><Briefcase size={10} style={{ display: "inline", marginRight: 3 }} />{person.total_companies || 0} companies</span>
            {person.strike_off_companies > 0 && <span style={{ color: "#ef4444" }}><AlertTriangle size={10} style={{ display: "inline", marginRight: 3 }} />{person.strike_off_companies} struck off</span>}
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); screenAllForPerson(); }}
          data-testid={`screen-all-person-${person.din}`}
          style={{ background: "rgba(239,68,68,0.12)", color: "#ef4444", border: "1px solid #ef444455", padding: "7px 13px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5, flexShrink: 0 }}
        ><ScanLine size={11} /> Screen All</button>
      </div>

      {/* Companies tree */}
      {expanded && (
        <div style={{ borderTop: "1px solid #1e2530", padding: 14 }}>
          {(person.individual_risk_signals || []).length > 0 && (
            <div style={{ marginBottom: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
              {person.individual_risk_signals.map((s, i) => (
                <span key={i} style={{ ...pillBase, background: `${RISK_COLOR[s.severity] || "#94a3b8"}18`, color: RISK_COLOR[s.severity] || "#94a3b8", border: `1px solid ${RISK_COLOR[s.severity] || "#94a3b8"}55` }}>
                  {s.signal}
                </span>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between mb-2">
            <div style={LABEL}><Network size={11} style={{ display: "inline", marginRight: 4 }} />Companies Tree ({(person.companies_tree || []).length})</div>
            <label style={{ fontSize: 10, color: "#94a3b8", display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
              <input
                type="checkbox"
                data-testid={`select-all-person-${person.din}`}
                checked={allSelected && (person.companies_tree || []).length > 0}
                ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                onChange={(e) => toggleAllForPerson(e.target.checked)}
                style={{ cursor: "pointer" }}
              />
              Select all
            </label>
          </div>

          {(person.companies_tree || []).length === 0 ? (
            <div style={{ fontSize: 11, color: "#64748b", padding: 10 }}>No companies associated</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {person.companies_tree.map((c, i) => {
                const cr = RISK_COLOR[(c.quick_risk_hint || "LOW").toUpperCase()] || "#22c55e";
                const struck = (c.company_status_mca || "").toLowerCase().includes("strike");
                return (
                  <label key={i} data-testid={`company-row-${c.cin}`} style={{ background: "#080c12", border: `1px solid ${struck ? "#ef444455" : "#1e2530"}`, borderRadius: 6, padding: 10, display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      data-testid={`select-cin-${c.cin}`}
                      checked={!!selectedCins[c.cin]}
                      onChange={() => toggleCin(c.cin)}
                      style={{ cursor: "pointer", flexShrink: 0 }}
                    />
                    <Building2 size={13} style={{ color: cr, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "#f1f5f9" }}>{c.company_name}</div>
                      <div style={{ fontSize: 10, color: "#64748b", display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <code>{c.cin}</code>
                        <span>{c.designation}</span>
                        {c.appointment_date && <span>Appt: {c.appointment_date}</span>}
                        {c.resignation_date && <span style={{ color: "#f59e0b" }}>Resigned: {c.resignation_date}</span>}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <span style={{ ...pillBase, background: struck ? "rgba(239,68,68,0.15)" : "rgba(34,197,94,0.12)", color: struck ? "#ef4444" : "#22c55e", border: struck ? "1px solid #ef444455" : "1px solid #22c55e55" }}>{c.company_status_mca}</span>
                      <span style={{ ...pillBase, background: `${cr}18`, color: cr, border: `1px solid ${cr}55` }}>{(c.quick_risk_hint || "LOW").toUpperCase()}</span>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const pillBase = {
  fontSize: 9, padding: "2px 7px", borderRadius: 3, fontWeight: 700,
  letterSpacing: 0.4, textTransform: "uppercase", whiteSpace: "nowrap",
};
